import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Pause, Pencil, Play, Trash2, UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/api/client";
import { useActivateStore, useDeactivateStore, useRemoveStoreMember, useSetStoreMember } from "@/api/mutations";
import { useStore } from "@/api/queries";
import type { StoreDetail, StoreRole } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { AuditTable } from "@/components/audit/audit-table";
import { DealerAppActions } from "@/components/audit/dealer-app-actions";
import { WorkAsDealerButton } from "@/components/dealer/work-as-dealer-button";
import { ConfirmAction } from "@/components/feedback/confirm-dialog";
import { EmptyState } from "@/components/feedback/empty-state";
import { IdBadge } from "@/components/feedback/id-badge";
import { KeyValueList, Section } from "@/components/feedback/key-value";
import { ErrorPanel } from "@/components/feedback/query-state";
import { StatusBadge } from "@/components/feedback/status-badge";
import { PageBody, PageHeader } from "@/components/layout/page-header";
import { IntegrationLink, OrgLink, UserLink } from "@/components/navigation/entity-links";
import { OpportunitiesTable } from "@/components/opportunity/opportunities-table";
import { PurchasesTable } from "@/components/opportunity/purchases-table";
import { Button } from "@/components/ui/button";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { AddStoreAccessDialog, STORE_ROLES } from "@/features/users/user-dialogs";
import { useLocalList } from "@/hooks/use-local-list";
import { formatDateTime } from "@/lib/dates";
import { formatNumber, formatPhone } from "@/lib/formatters";
import { INTEGRATION, ROLE_LABEL, STORE } from "@/lib/status";
import { cn } from "@/lib/utils";
import { AppointmentsTable } from "../appointments/appointments-table";
import { StoreAppointmentSettings } from "./store-appointment-settings";
import { StoreFormDialog } from "./store-form-dialog";
import { StoreOfferIntegrations } from "./store-offer-integrations";
import { StorePolicyDialog } from "./store-policy-dialog";

export const STORE_SECTIONS = [
  "overview",
  "users",
  "buyers",
  "opportunities",
  "appointments",
  "purchases",
  "integrations",
  "rules",
  "activity",
  "admin-actions",
] as const;
const SECTION_LABEL: Partial<Record<(typeof STORE_SECTIONS)[number], string>> = { "admin-actions": "Admin actions" };
type S = (typeof STORE_SECTIONS)[number];

export function StoreDetailPage() {
  const { storeId } = useParams({ from: "/_authenticated/stores/$storeId" });
  const { section = "overview" } = useSearch({ from: "/_authenticated/stores/$storeId" });
  const q = useStore(storeId);
  const { can } = usePermissions();
  const [dialog, setDialog] = useState<null | "edit" | "deactivate" | "activate">(null);
  const deactivate = useDeactivateStore(storeId);
  const activate = useActivateStore(storeId);
  const s = q.data;
  if (q.isError && !s)
    return (
      <PageBody>
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} resource="store" />
      </PageBody>
    );
  const done = (msg: string) => ({
    onSuccess: () => {
      toast.success(msg);
      setDialog(null);
    },
    onError: (err: unknown) => toast.error("Action failed", { description: errorMessage(err) }),
  });
  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Organizations", to: "/organizations" },
          ...(s ? [{ label: s.organization.name, to: `/organizations/${s.organization.id}`, search: { tab: "stores" } }] : []),
          { label: s?.name ?? "…" },
        ]}
        title={
          s ? (
            <span className="flex items-center gap-2">
              {s.name} <StatusBadge config={STORE[s.status]} />
            </span>
          ) : (
            <Skeleton className="h-5 w-48" />
          )
        }
        description={s?.extra ?? undefined}
        meta={
          s ? (
            <>
              <span>
                Organization <OrgLink org={s.organization} />
              </span>
              <span>
                Store ID <IdBadge id={s.id} full className="text-foreground" />
              </span>
              <span>{s.location ?? "—"}</span>
              <span>
                SMS{" "}
                {[
                  ["Twilio", s.phoneNumber],
                  ["Infobip", s.infobipNumber],
                  ["Telnyx", s.telnyxNumber],
                ]
                  .filter(([, n]) => n)
                  .map(([label, n]) => `${formatPhone(n)} (${label})`)
                  .join(" · ") || "—"}
              </span>
            </>
          ) : null
        }
        actions={
          s ? (
            <>
              {/* Deactivated stores are hidden from dealer sessions: nothing to open. */}
              {s.status !== "INACTIVE" ? <WorkAsDealerButton organizationId={s.organization.id} storeId={s.id} label="Open as dealer" /> : null}
              {can("stores:manage") ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setDialog("edit")}>
                    <Pencil data-icon="inline-start" />
                    Edit
                  </Button>
                  {s.status === "INACTIVE" ? (
                    <Button size="sm" variant="outline" onClick={() => setDialog("activate")}>
                      <Play data-icon="inline-start" />
                      Activate
                    </Button>
                  ) : s.status === "ACTIVE" ? (
                    <Button size="sm" variant="destructive" onClick={() => setDialog("deactivate")}>
                      <Pause data-icon="inline-start" />
                      Deactivate
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null
        }
      >
        <nav aria-label="Store sections" className="-mb-2.5 flex gap-0.5 overflow-x-auto">
          {STORE_SECTIONS.map((t) => (
            <Link
              key={t}
              from="/stores/$storeId"
              to="."
              search={{ section: t === "overview" ? undefined : t }}
              aria-current={t === section ? "page" : undefined}
              className={cn(
                "shrink-0 border-b-2 px-2.5 pb-2 text-xs capitalize",
                t === section ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {SECTION_LABEL[t] ?? t}
            </Link>
          ))}
        </nav>
      </PageHeader>
      <PageBody>{s ? <Body section={section} store={s} /> : <Skeleton className="h-64" />}</PageBody>

      {s ? <StoreFormDialog open={dialog === "edit"} onOpenChange={(o) => setDialog(o ? "edit" : null)} organization={s.organization} store={s} /> : null}
      {s && dialog === "deactivate" ? (
        <ConfirmAction
          open
          onOpenChange={(o) => !o && setDialog(null)}
          action="Deactivate store"
          resource={{ type: "Store", name: s.name, id: s.id }}
          consequences={[
            "Dealer users lose access to this store (other stores are unaffected).",
            "Rogue stops replying to its sellers; incoming texts are still recorded.",
            "Members, opportunities and history are kept. Recorded in the audit log.",
          ]}
          requireReason
          requireTypedName
          confirmLabel="Deactivate store"
          pending={deactivate.isPending}
          onConfirm={(reason) => deactivate.mutate(reason, done(`${s.name} deactivated`))}
        />
      ) : null}
      {s && dialog === "activate" ? (
        <ConfirmAction
          open
          onOpenChange={(o) => !o && setDialog(null)}
          action="Activate store"
          destructive={false}
          resource={{ type: "Store", name: s.name, id: s.id }}
          consequences={["Members regain access and Rogue resumes on new seller messages."]}
          confirmLabel="Activate"
          pending={activate.isPending}
          onConfirm={() => activate.mutate(undefined, done(`${s.name} activated`))}
        />
      ) : null}
    </>
  );
}

type MemberConfirm = null | { kind: "remove"; userId: string; name: string } | { kind: "role"; userId: string; name: string; from: StoreRole; to: StoreRole };

function Members({ store, buyersOnly }: { store: StoreDetail; buyersOnly?: boolean }) {
  const { can } = usePermissions();
  const manage = can("users:manage");
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<MemberConfirm>(null);
  const setMember = useSetStoreMember(store.id);
  const remove = useRemoveStoreMember(store.id);
  const members = buyersOnly ? store.members.filter((m) => m.role === "BUYER") : store.members;
  const done = (msg: string) => ({
    onSuccess: () => {
      toast.success(msg);
      setConfirm(null);
    },
    onError: (err: unknown) => toast.error("Action failed", { description: errorMessage(err) }),
  });
  return (
    <>
      <Section
        title={buyersOnly ? "Buyers" : `Members (${members.length})`}
        className="[&>div]:p-0"
        actions={
          manage ? (
            <Button size="xs" variant="outline" onClick={() => setAdding(true)}>
              <UserPlus data-icon="inline-start" />
              Add member
            </Button>
          ) : null
        }
      >
        {!members.length ? (
          <EmptyState
            icon={Users}
            title={buyersOnly ? "No buyers at this store" : "No members"}
            description={manage ? "Add people from this organization, or create them from the organization's Users tab." : "Nobody has access yet."}
          />
        ) : (
          <table className="w-full text-xs" aria-label={buyersOnly ? "Store buyers" : "Store users"}>
            <thead className="text-[11px] text-muted-foreground uppercase">
              <tr className="border-b">
                <th className="px-3 py-1.5 text-left font-medium">Name</th>
                <th className="px-3 text-left font-medium">Email</th>
                <th className="px-3 text-left font-medium">Role here</th>
                <th className="px-3 text-left font-medium">Other stores</th>
                {manage ? <th className="px-3 text-right font-medium">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId} className="border-b last:border-b-0">
                  <td className="px-3 py-1.5">
                    <UserLink user={{ id: m.userId, name: m.name }} />
                  </td>
                  <td className="px-3 font-mono">{m.email}</td>
                  <td className="px-3">
                    {manage ? (
                      <NativeSelect
                        size="sm"
                        aria-label={`Role of ${m.name}`}
                        value={m.role}
                        onChange={(e) =>
                          e.target.value !== m.role &&
                          setConfirm({ kind: "role", userId: m.userId, name: m.name, from: m.role, to: e.target.value as StoreRole })
                        }
                      >
                        {STORE_ROLES.map((r) => (
                          <NativeSelectOption key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </NativeSelectOption>
                        ))}
                      </NativeSelect>
                    ) : (
                      (ROLE_LABEL[m.role] ?? m.role)
                    )}
                  </td>
                  <td className="px-3 text-muted-foreground">
                    {m.otherStoreCount ? `Also in ${m.otherStoreCount} other store${m.otherStoreCount > 1 ? "s" : ""}` : "—"}
                  </td>
                  {manage ? (
                    <td className="px-3 text-right">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setConfirm({ kind: "remove", userId: m.userId, name: m.name })}
                        aria-label={`Remove ${m.name} from ${store.name}`}
                      >
                        <Trash2 data-icon="inline-start" />
                        Remove
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {adding ? (
        <AddStoreAccessDialog
          onClose={() => setAdding(false)}
          store={{ id: store.id, name: store.name, organization: store.organization, memberIds: store.members.map((m) => m.userId) }}
        />
      ) : null}
      {confirm?.kind === "remove" ? (
        <ConfirmAction
          open
          onOpenChange={(o) => !o && setConfirm(null)}
          action="Remove from store"
          resource={{ type: "User", name: confirm.name, id: confirm.userId }}
          consequences={[
            `${confirm.name} loses access to ${store.name}.`,
            "They stay in the organization and keep their other stores.",
            "Opportunities assigned to them here stay assigned until reassigned.",
          ]}
          confirmLabel="Remove"
          pending={remove.isPending}
          onConfirm={() => remove.mutate(confirm.userId, done(`${confirm.name} removed from ${store.name}`))}
        />
      ) : null}
      {confirm?.kind === "role" ? (
        <ConfirmAction
          open
          onOpenChange={(o) => !o && setConfirm(null)}
          action="Change role"
          destructive={false}
          resource={{ type: "User", name: confirm.name, id: confirm.userId }}
          consequences={[`${store.name}: ${ROLE_LABEL[confirm.from]} → ${ROLE_LABEL[confirm.to]}.`, "Permissions change on their next request."]}
          confirmLabel="Change role"
          pending={setMember.isPending}
          onConfirm={() => setMember.mutate({ userId: confirm.userId, role: confirm.to }, done("Role updated"))}
        />
      ) : null}
    </>
  );
}

function Body({ section, store }: { section: S; store: StoreDetail }) {
  const f = { storeId: store.id };
  const [opps, setOpps] = useLocalList(f, { sort: "updatedAt" });
  const [appts, setAppts] = useLocalList(f, { sort: "startsAt" });
  const [purchases, setPurchases] = useLocalList(f, { sort: "purchasedAt" });
  const [audit, setAudit] = useLocalList(f, { sort: "at" });
  const navigate = useNavigate();
  switch (section) {
    case "users":
      return <Members store={store} />;
    case "buyers":
      return <Members store={store} buyersOnly />;
    case "opportunities":
      return <OpportunitiesTable query={opps} onChange={setOpps} hide={["organization", "store"]} />;
    case "appointments":
      return <AppointmentsTable query={appts} onChange={setAppts} hide={["organization", "store"]} />;
    case "purchases":
      return (
        <PurchasesTable
          query={purchases}
          onChange={setPurchases}
          hide={["organization", "store"]}
          onRowClick={(id) => void navigate({ to: "/purchases", search: { trace: id } })}
        />
      );
    case "admin-actions":
      return <DealerAppActions filters={{ storeId: store.id }} title="What Rogue admins did in the dealer app" showLead />;
    case "activity":
      return (
        <AuditTable
          query={audit}
          onChange={setAudit}
          hide={["organization", "store"]}
          onRowClick={(id) => void navigate({ to: "/audit", search: { event: id } })}
        />
      );
    case "integrations":
      return (
        <div className="space-y-4">
          <StoreOfferIntegrations store={store} />
          <Section title="Platform integrations (via organization)" className="[&>div]:p-0">
            <ul className="divide-y">
              {store.integrations
                .filter((i) => !(store.offerIntegrations ?? []).some((o) => o.integrationId === i.id))
                .map((i) => (
                  <li key={i.id} className="flex items-center justify-between px-3 py-1.5">
                    <IntegrationLink id={i.id} name={i.name} />
                    <StatusBadge config={INTEGRATION[i.status]} />
                  </li>
                ))}
            </ul>
          </Section>
        </div>
      );
    case "rules":
      return <Rules store={store} />;
    default:
      return (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Store">
            <KeyValueList
              items={[
                ["Name", store.name],
                ["Extra details", store.extra ?? "—"],
                ["Address", store.address ?? "—"],
                ["City", store.city ?? "—"],
                ["State", store.state ?? "—"],
                ["ZIP code", store.postalCode ?? "—"],
                ["Time zone", store.timezone.replaceAll("_", " ")],
                ["Email", store.email ?? "—"],
                ["Store phone", formatPhone(store.contactPhone)],
                ["Twilio number", formatPhone(store.phoneNumber)],
                ["Infobip number", formatPhone(store.infobipNumber)],
                ["Telnyx number", formatPhone(store.telnyxNumber)],
                ["New leads text from", store.defaultSmsProvider ? { TWILIO: "Twilio", INFOBIP: "Infobip", TELNYX: "Telnyx" }[store.defaultSmsProvider] : "—"],
                ["Status", <StatusBadge key="s" config={STORE[store.status]} />],
                ["Deactivated", store.deactivatedAt ? `${formatDateTime(store.deactivatedAt)} — ${store.deactivationReason ?? "no reason"}` : "—"],
                ["Created", formatDateTime(store.createdAt)],
              ]}
            />
          </Section>
          <Section title="Activity">
            <KeyValueList
              items={[
                ["Users", formatNumber(store.userCount)],
                ["Buyers", formatNumber(store.buyerCount)],
                ["Active opportunities", formatNumber(store.activeOpportunities)],
                ["Appointments (±7d)", formatNumber(store.appointments7d)],
                ["Purchases (30d)", formatNumber(store.purchases30d)],
              ]}
            />
          </Section>
        </div>
      );
  }
}

/** The store's rules. The offer policy and appointment settings are edited here (stores:manage); dealers only read the offer policy. */
function Rules({ store }: { store: StoreDetail }) {
  const { can } = usePermissions();
  const [editing, setEditing] = useState(false);
  return (
    <>
      <Section
        title="Store rules"
        actions={
          can("stores:manage") ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil data-icon="inline-start" />
              Edit offer policy
            </Button>
          ) : null
        }
      >
        <KeyValueList items={store.rules.map((r) => [r.label, r.value])} />
      </Section>
      <StorePolicyDialog open={editing} onOpenChange={setEditing} store={store} />
      <StoreAppointmentSettings store={store} />
    </>
  );
}
